import {applyWorldTransaction} from './world';
import type {JsonObject,JsonValue,WorldDocument,WorldTransaction} from './types';

export type TurnFieldChange={path:string[];op:'set';value:JsonValue}|{path:string[];op:'unset'};
export interface TurnFileDelta {documentId:string;expectedRevision:number;revision:number;changes:TurnFieldChange[]}
/** Transport format for the existing durable turn/job record, not a second store. */
export interface CompactTurnOutcome {version:1;transactionId:string;storyId:string;createdAt:string;files:TurnFileDelta[]}
const record=(value:unknown):value is JsonObject=>!!value&&typeof value==='object'&&!Array.isArray(value);
const forbidden=new Set(['__proto__','prototype','constructor']);
const protectedKeys=new Set(['id','storyId','revision']);
const serializable=(value:unknown)=>JSON.parse(JSON.stringify(value)) as JsonObject;

function changes(before:JsonValue|undefined,after:JsonValue|undefined,path:string[],result:TurnFieldChange[]) {
 if(JSON.stringify(before)===JSON.stringify(after))return;
 if(record(before)&&record(after)) {
  for(const key of [...new Set([...Object.keys(before),...Object.keys(after)])].sort()) {
   if(forbidden.has(key))throw new Error('Unsafe turn field path');
   changes(before[key],after[key],[...path,key],result);
  }
 }else result.push(after===undefined?{path,op:'unset'}:{path,op:'set',value:structuredClone(after)});
 if(result.length>4096)throw new Error('Turn has too many field changes');
}

export function compactTurnOutcome(before:readonly WorldDocument[],transaction:WorldTransaction):CompactTurnOutcome {
 const result=applyWorldTransaction(before,transaction);
 const files=result.events.map(event=>{
  const old=event.before?serializable(event.before):{};
  const next=serializable(event.after);
  for(const key of protectedKeys){delete old[key];delete next[key];}
  const delta:TurnFieldChange[]=[];changes(old,next,[],delta);
  return {documentId:event.documentId,expectedRevision:event.beforeRevision,revision:event.afterRevision,changes:delta};
 });
 return {version:1,transactionId:transaction.id,storyId:transaction.storyId,createdAt:transaction.createdAt,files};
}

/** Compose confirmed pending outcomes before a next turn reads the files. CAS
 * prevents merging a delta into an unrelated newer version of the same file. */
export function overlayTurnOutcomes(documents:readonly WorldDocument[],outcomes:readonly CompactTurnOutcome[]):WorldDocument[] {
 const current=new Map(documents.map(document=>[`${document.storyId}\0${document.id}`,structuredClone(document)]));
 const seen=new Map<string,string>();
 for(const outcome of outcomes) {
  if(outcome.version!==1)throw new Error('Unsupported turn outcome version');
  const identity=`${outcome.storyId}\0${outcome.transactionId}`,encoded=JSON.stringify(outcome);
  if(seen.has(identity)){if(seen.get(identity)!==encoded)throw new Error('Turn outcome identity conflict');continue;}
  seen.set(identity,encoded);
  const ids=new Set<string>();
  for(const file of outcome.files) {
   if(ids.has(file.documentId))throw new Error('Duplicate turn file');ids.add(file.documentId);
   const key=`${outcome.storyId}\0${file.documentId}`,before=current.get(key);
   if((before?.revision??0)!==file.expectedRevision||!Number.isSafeInteger(file.expectedRevision)||file.expectedRevision<0||file.revision!==file.expectedRevision+1)throw new Error(`Turn revision conflict: ${file.documentId}`);
   const next=before?serializable(before):{};
   for(const change of file.changes) {
    if(!change.path.length||protectedKeys.has(change.path[0]!)||change.path.some(key=>forbidden.has(key)))throw new Error('Unsafe turn field path');
    let parent:JsonObject=next;
    for(const part of change.path.slice(0,-1)) {
     if(!Object.hasOwn(parent,part)||!record(parent[part]))throw new Error('Turn field parent is missing');
     parent=parent[part] as JsonObject;
    }
    const leaf=change.path.at(-1)!;
    if(change.op==='unset')delete parent[leaf];else parent[leaf]=structuredClone(change.value);
   }
   current.set(key,{...next,id:file.documentId,storyId:outcome.storyId,revision:file.revision} as unknown as WorldDocument);
  }
 }
 return [...current.values()];
}
